import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  Query,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "./firebase_config";
import { Constants } from "../constant";
import type {
  CreateDynamicLinkInput,
  DynamicLinkListItem,
  UpdateDynamicLinkInput,
} from "../types/dynamicLinks";
import type { DynamicLinkStatus } from "../types/dynamicLinks";
import {
  appendActivity,
  ActivityDomains,
  type ActivityActor,
  type ActivityAppendInput,
} from "./activityService";

export const DYNAMIC_LINK_SLUG_PATTERN = /^[a-z0-9-]{1,64}$/;

async function logActivitySafe(entry: ActivityAppendInput): Promise<void> {
  try {
    await appendActivity(entry);
  } catch (e) {
    console.error("Failed to record activity:", e);
  }
}

function buildShortUrl(slug: string): string {
  return `${Constants.DYNAMIC_LINKS_PUBLIC_BASE}/dl/${slug}`;
}

/** Valid http(s) URL with bounded length (aligned with rules). */
export function isValidHttpUrl(raw: string): boolean {
  const s = raw.trim();
  if (!s || s.length > 2048) return false;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export function normalizeSlugInput(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function randomSlugSegment(length: number): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += chars[bytes[i]! % chars.length];
  }
  return out;
}

async function slugExists(slug: string): Promise<boolean> {
  const snap = await getDoc(
    doc(firestore, Constants.DYNAMIC_LINKS_COLLECTION, slug),
  );
  return snap.exists();
}

/** Reserve a unique slug; retries on collision. */
export async function allocateUniqueSlug(preferred?: string): Promise<string> {
  if (preferred) {
    if (!DYNAMIC_LINK_SLUG_PATTERN.test(preferred)) {
      throw new Error(
        "Slug must be 1–64 characters: lowercase letters, numbers, and dashes only.",
      );
    }
    if (await slugExists(preferred)) {
      throw new Error("This slug is already in use. Choose another.");
    }
    return preferred;
  }
  for (let attempt = 0; attempt < 12; attempt++) {
    const candidate = randomSlugSegment(10);
    if (!(await slugExists(candidate))) return candidate;
  }
  throw new Error("Could not generate a unique slug. Try again.");
}

function parseExpiresAt(local: string | undefined): Timestamp | null {
  if (!local || !local.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) {
    throw new Error("Invalid expiration date.");
  }
  return Timestamp.fromDate(d);
}

export async function createDynamicLink(
  input: CreateDynamicLinkInput,
  uid: string,
  actor?: ActivityActor | null,
): Promise<{ slug: string; shortUrl: string }> {
  const androidUrl = input.androidUrl.trim();
  const iosUrl = input.iosUrl.trim();
  const fallbackUrl = input.fallbackUrl.trim();
  if (!isValidHttpUrl(androidUrl))
    throw new Error("Android target URL is not a valid http(s) URL.");
  if (!isValidHttpUrl(iosUrl))
    throw new Error("iOS target URL is not a valid http(s) URL.");
  if (!isValidHttpUrl(fallbackUrl))
    throw new Error("Fallback URL is not a valid http(s) URL.");

  const normalizedManual = input.slug?.trim()
    ? normalizeSlugInput(input.slug)
    : "";
  const slug = await allocateUniqueSlug(normalizedManual || undefined);

  const status: DynamicLinkStatus =
    input.status === "paused" ? "paused" : "active";
  const expiresAt = parseExpiresAt(input.expiresAtLocal);
  const shortUrl = buildShortUrl(slug);
  const nameTrim = input.name?.trim();
  const creatorDisplayName = input.creatorDisplayName.trim() || uid;

  const payload: Record<string, unknown> = {
    slug,
    shortUrl,
    androidUrl,
    iosUrl,
    fallbackUrl,
    status,
    createdBy: uid,
    createdByDisplayName: creatorDisplayName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    clickCount: 0,
  };
  if (nameTrim) payload.name = nameTrim;
  if (expiresAt) payload.expiresAt = expiresAt;

  await setDoc(
    doc(firestore, Constants.DYNAMIC_LINKS_COLLECTION, slug),
    payload,
  );
  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.DynamicLinks,
      action: "create",
      resourceType: "dynamic_link",
      resourceId: slug,
      summary: `Created dynamic link /dl/${slug}`,
      actor,
      details: { shortUrl, status },
    });
  }

  return { slug, shortUrl };
}

export async function updateDynamicLink(
  slug: string,
  uid: string,
  input: UpdateDynamicLinkInput,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.DYNAMIC_LINKS_COLLECTION, slug);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("Link not found.");
  }
  const existing = snap.data() as { createdBy?: string };
  if (existing.createdBy !== uid) {
    throw new Error("You can only edit your own links.");
  }

  const androidUrl = input.androidUrl.trim();
  const iosUrl = input.iosUrl.trim();
  const fallbackUrl = input.fallbackUrl.trim();
  if (!isValidHttpUrl(androidUrl))
    throw new Error("Android target URL is not a valid http(s) URL.");
  if (!isValidHttpUrl(iosUrl))
    throw new Error("iOS target URL is not a valid http(s) URL.");
  if (!isValidHttpUrl(fallbackUrl))
    throw new Error("Fallback URL is not a valid http(s) URL.");

  const status: DynamicLinkStatus =
    input.status === "paused" ? "paused" : "active";
  const expiresAt = parseExpiresAt(input.expiresAtLocal);
  const shortUrl = buildShortUrl(slug);
  const nameTrim = input.name?.trim();

  await updateDoc(ref, {
    androidUrl,
    iosUrl,
    fallbackUrl,
    shortUrl,
    status,
    updatedAt: serverTimestamp(),
    name: nameTrim ? nameTrim : deleteField(),
    expiresAt: expiresAt ? expiresAt : deleteField(),
  });

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.DynamicLinks,
      action: "update",
      resourceType: "dynamic_link",
      resourceId: slug,
      summary: `Updated dynamic link /dl/${slug}`,
      actor,
      details: { shortUrl, status },
    });
  }
}

export async function deleteDynamicLink(
  slug: string,
  uid: string,
  actor?: ActivityActor | null,
): Promise<void> {
  const ref = doc(firestore, Constants.DYNAMIC_LINKS_COLLECTION, slug);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new Error("Link not found.");
  }
  const existing = snap.data() as { createdBy?: string };
  if (existing.createdBy !== uid) {
    throw new Error("You can only delete your own links.");
  }

  await deleteDoc(ref);

  if (actor) {
    await logActivitySafe({
      domain: ActivityDomains.DynamicLinks,
      action: "delete",
      resourceType: "dynamic_link",
      resourceId: slug,
      summary: `Deleted dynamic link /dl/${slug}`,
      actor,
    });
  }
}

/** `datetime-local` value from a Firestore timestamp (local timezone). */
export function timestampToDatetimeLocalValue(ts: Timestamp): string {
  const d = ts.toDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export async function listMyDynamicLinks(
  uid: string,
  isAdmin = false,
): Promise<DynamicLinkListItem[]> {
  let q: Query<DocumentData, DocumentData>;
  if (isAdmin) {
    q = query(collection(firestore, Constants.DYNAMIC_LINKS_COLLECTION));
  } else {
    q = query(
      collection(firestore, Constants.DYNAMIC_LINKS_COLLECTION),
      where("createdBy", "==", uid),
    );
  }
  const snap = await getDocs(q);
  const rows: DynamicLinkListItem[] = [];
  snap.forEach((d) => {
    const data = d.data() as DynamicLinkListItem;
    rows.push({ ...data, id: d.id });
  });
  rows.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
  return rows;
}

/** Deletes owned links whose expiresAt is in the past. */
export async function deleteExpiredOwnedLinks(uid: string): Promise<number> {
  const now = Date.now();
  const links = await listMyDynamicLinks(uid);
  let removed = 0;
  for (const link of links) {
    const ex = link.expiresAt;
    if (ex && ex.toMillis() < now) {
      await deleteDoc(
        doc(firestore, Constants.DYNAMIC_LINKS_COLLECTION, link.slug),
      );
      removed += 1;
    }
  }
  return removed;
}
